// Workspace Service API Types

// === ENUMS ===

export enum UserRole {
  Admin = 1,
  Participant = 2,
  Guest = 3,
}

export type PricingModelType = 'hourly-price-by-participants-count';

// === MAIN ENTITIES ===

export interface User {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  tgUsername: string | null;
  country: string | null;
  languageCode: string | null;
  skillLevel: number | null;
  workspaceId: string;
  photoUrl: string | null;
  roles: UserRole[];
  birthday: string | null;
  createdAt: string;
}

export interface SkillLevel {
  workspaceId: string;
  name: string;
  weight: number;
  settings?: {
    iconId?: string;
  };
  createdAt: string;
}

export interface WorkspaceSettings {
  freeCancellationDeadlineMinutes: number; // Default: 24 * 60
  // cancellationPenaltyPercent: number; // Default: 50
  confirmationStartTimeMinutes: number; // Time before the event when confirmations are available
  maxLevelDifferenceForReplacement: number; // Max difference between replacing user level and event level
  maxParticipantsPerEvent: number; // Max participants count
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  settings: WorkspaceSettings;
  createdAt: string;
}

export interface HourlyPriceByParticipantsCountPricingModelConfig {
  1: number; // Price for 1 participant per hour
  2: number; // Price for 2 participants per hour
  3: number; // Price for 3 participants per hour
  4: number; // Price for 4 participants per hour
  5: number; // Price for 5 participants per hour
  6: number; // Price for 6 participants per hour
}

export type PricingModelConfig =
  HourlyPriceByParticipantsCountPricingModelConfig;

export interface PricingModel {
  id: string;
  type: PricingModelType;
  config: PricingModelConfig;
  createdAt: string;
  updatedAt: string;
}

// === REQUEST/RESPONSE TYPES ===

// Workspace Management
export interface GetWorkspaceInfoResponse {
  id: string;
  name: string;
  slug: string;
  iconUrl: string | null;
  createdAt: string;
  settings?: WorkspaceSettings; // Only for Admin users (or now for all if API returns)
}

export interface GetWorkspacesByAuthProfileResponse {
  workspaces: Workspace[];
}

export interface UpdateWorkspaceRequest {
  name?: string;
  description?: string;
  slug?: string;
  settings?: Partial<WorkspaceSettings>;
}

export interface UpdateWorkspaceResponse {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  settings?: WorkspaceSettings; // Only for Admin users
}

// User Management
export interface GetMeResponse {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  tgUsername: string | null;
  country: string | null;
  languageCode: string | null;
  skillLevel: number | null;
  workspaceId: string;
  photoUrl: string | null;
  roles: UserRole[];
  birthday: string | null;
  createdAt: string;
}

export interface GetUserResponse {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  country: string | null;
  languageCode: string | null;
  skillLevel: number | null;
  workspaceId: string;
  photoUrl: string | null;
  roles: UserRole[];
  birthday: string | null;
  createdAt: string;
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  phone?: string;
  country?: string;
  languageCode?: string;
  skillLevel?: number; // Only admin can update
  birthday?: string;
}

export interface UpdateUserResponse {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  tgUsername: string | null;
  country: string | null;
  languageCode: string | null;
  skillLevel: number | null;
  workspaceId: string;
  photoUrl: string | null;
  roles: UserRole[];
  birthday: string | null;
  createdAt: string;
}

export interface GetUsersParams {
  namePattern?: string;
  skillLevel?: number;
}

export interface GetUsersResponse {
  users: User[];
}

// Skill Levels
export interface GetSkillLevelsResponse {
  levels: SkillLevel[];
}

export interface UpdateSkillLevelsRequest {
  levels: {
    name: string;
    weight: number;
    settings?: {
      iconId?: string;
    };
  }[];
}

export interface UpdateSkillLevelsResponse {
  levels: SkillLevel[];
}

// Pricing Models
export interface GetPricingModelsResponse {
  models: PricingModel[];
}

export interface GetPricingModelResponse {
  model: PricingModel;
}

export interface UpdatePricingModelRequest {
  config: PricingModelConfig;
}

export interface UpdatePricingModelResponse {
  model: PricingModel;
}
